import { Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Select } from '@/components/ui/select';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { useLanguage } from '@/i18n/language-context';
import { readStored } from './http';
import { useDonorCopy } from './copy';

type Theme = 'light' | 'dark' | 'system';

function savedTheme(): Theme {
  const saved = readStored<unknown>('giving.theme', 'light');
  return saved === 'dark' || saved === 'system' ? saved : 'light';
}

export function ThemeControl(): JSX.Element {
  const { t } = useDonorCopy();
  const { locale } = useLanguage();
  const [theme, setTheme] = useState<Theme>(savedTheme);
  const options = [
    { value: 'light', label: t.light },
    { value: 'dark', label: t.dark },
    { value: 'system', label: t.system },
  ];
  const label = options.find((option) => option.value === theme)?.label ?? t.light;
  const Icon = theme === 'dark' ? Moon : theme === 'system' ? Monitor : Sun;
  useEffect(() => {
    const media = matchMedia('(prefers-color-scheme: dark)');
    const update = (): void => {
      document.documentElement.dataset['theme'] =
        theme === 'system' ? (media.matches ? 'dark' : 'light') : theme;
    };
    update();
    media.addEventListener('change', update);
    try {
      localStorage.setItem('giving.theme', JSON.stringify(theme));
    } catch {
      /* Current visit still works. */
    }
    return () => media.removeEventListener('change', update);
  }, [theme, locale]);
  return (
    <>
      <Select
        className="theme-control header-preference-trigger header-preference--compact"
        popupClassName="header-preference-popup"
        aria-label={`${t.theme}: ${label}`}
        value={theme}
        onValueChange={(value) => {
          if (value === 'light' || value === 'dark' || value === 'system') setTheme(value);
        }}
        options={options}
        triggerContent={<Icon size={19} aria-hidden="true" />}
      />
      <ToggleGroup<Theme>
        className="theme-control header-preference--expanded"
        label={t.theme}
        value={theme}
        onValueChange={setTheme}
        options={[
          { value: 'light', label: t.light, content: <Sun size={18} aria-hidden="true" /> },
          { value: 'dark', label: t.dark, content: <Moon size={18} aria-hidden="true" /> },
          { value: 'system', label: t.system, content: <Monitor size={18} aria-hidden="true" /> },
        ]}
      />
    </>
  );
}
